import 'prosemirror-view/style/prosemirror.css';
import 'katex/dist/katex.min.css';
import '@benrbray/prosemirror-math/dist/prosemirror-math.css';
import './typography.css';
import './collab-cursor.css';

import { cx } from '@bangle.io/base-utils';
import React, { useCallback } from 'react';
import {
  CalloutMenu,
  DatePickerMenu,
  EditorContextMenu,
  EditorImageLightbox,
  HeadingTocRail,
  InlineSelectionMenu,
  LinkMenu,
  SlashCommand,
  TableMenu,
  UserMentionMenu,
  WikiLinkMenu,
} from './components';
import { useEditorCoreServices } from './use-editor-core-services';

export {
  $collabCommentThreads,
  $collabPeers,
  $collabStatus,
  $collabYdoc,
  $pendingPresenceJump,
  $stickyCloudWorkspace,
  $workspacePresencePeers,
  abandonActiveCollabSession,
  getActiveCollabSession,
  setActiveCollabSession,
} from './collab-state';
export {
  createEditorSaveCoordinator,
  type EditorSaveCoordinator,
  type EditorSaveStatus,
  flushUnsavedDraftsToLocalStorage,
} from './editor-save-queue';
export { setupExtensions } from './extensions';
export {
  $imageLightbox,
  type ImageLightboxState,
} from './image-lightbox-state';
export {
  PmEditorService,
  type PmEditorServiceConfig,
} from './pm-editor-service';
/**
 * The editor factory, exported for component-level test harnesses that mount
 * a real editor outside the app shell.
 */
export { createEditor } from './pm-setup';
export {
  ReadonlyMarkdown,
  type ReadonlyMarkdownProps,
} from './readonly-markdown';
export {
  createReadonlyMarkdownView,
  type ReadonlyMarkdownViewOptions,
} from './readonly-markdown-view';
export { useEditorCoreServices } from './use-editor-core-services';
export function Editor({
  wsPath,
  className,
  name,
  readOnly = false,
}: {
  wsPath: string;
  className?: string;
  name: string;
  readOnly?: boolean;
}) {
  const { editorEngine } = useEditorCoreServices();

  return (
    <div className="box-border flex h-full min-h-36 w-full min-w-0 flex-col">
      <div className="relative box-border w-full min-w-0 flex-1">
        <div
          // in react 19 callback change retriggers this callback
          // so we use useCallback to memoize the callback
          ref={useCallback(
            (node: HTMLElement | null) => {
              const cleanup =
                node &&
                editorEngine.mountEditor({
                  domNode: node,
                  wsPath,
                  name,
                  readOnly,
                });

              return () => {
                cleanup?.();
              };
            },
            [name, readOnly, wsPath, editorEngine],
          )}
          data-editor-name={name}
          data-editor-engine={editorEngine.engineId}
          className={cx(
            'ProseMirror box-border min-h-full min-w-0 max-w-full py-6 outline-0 outline-hidden',
            '[&_:not(pre)_code]:rounded-md [&_:not(pre)_code]:bg-muted/40 [&_:not(pre)_code]:px-1.5 [&_:not(pre)_code]:py-0.5 [&_:not(pre)_code]:font-mono',
            "[&_pre]:bg-muted/30 [&_span[data-mention='tag']]:text-primary [&_span[data-mention='user']]:text-accent",
            className,
          )}
        />
        <SlashCommand editorName={name} />
        <DatePickerMenu editorName={name} />
        <TableMenu editorName={name} />
        <CalloutMenu editorName={name} />
        <WikiLinkMenu editorName={name} />
        <UserMentionMenu editorName={name} />
        <LinkMenu editorName={name} />
        <InlineSelectionMenu editorName={name} />
        {/* Keyed by note so a menu opened for one note cannot outlive a
            switch to another. */}
        <EditorContextMenu key={name} editorName={name} />
        <HeadingTocRail editorName={name} />
        <EditorImageLightbox />
      </div>
    </div>
  );
}
